import React, { useState, useRef, useEffect } from 'react';
import { Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import SmsChargeModal from './SmsChargeModal';
import SmsTemplateSelectModal from './SmsTemplateSelectModal';
import MemberRecipientPickerModal from '../MemberRecipientPickerModal';
import SmsSendConfirmModal from './SmsSendConfirmModal';
import { getSmsBalance, sendSms, sendSmsBulk, getVerifiedSmsSenders } from '../../../../api/sms';
import { uploadImages } from '../../../../api/image';
import { formatterCurrency } from '../../../../utils/methods';
import CommonSelect from '../../../../form-components/CommonSelect';

import {
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconButton,
  ModalContentArea,
  ContentRow,
  LeftSection,
  RightSection,
  FormRow,
  FormLabel,
  SelectBox,
  SelectText,
  PlaceholderText,
  ViewListButton,
  AdExcludeNotice,
  AdExcludeHint,
  ContentSection,
  ContentTitleRow,
  TemplateButton,
  TemplateButtonText,
  MessageTextArea,
  AdCheckboxRow,
  AdCheckbox,
  AdCheckboxLabel,
  PhotoRow,
  PhotoLabelRow,
  PhotoFieldColumn,
  PhotoSpecHint,
  PhotoAttachArea,
  PhotoDataBox,
  PhotoDataText,
  PhotoChipList,
  PhotoChip,
  PhotoChipText,
  PhotoCountBadge,
  PhotoRemoveButton,
  PhotoAttachButton,
  PreviewContainer,
  PreviewTitle,
  PreviewContent,
  PreviewText,
  PreviewAdMark,
  PreviewAdFooter,
  PreviewImage,
  PreviewImageList,
  RemainingRow,
  RemainingInfo,
  RemainingLabel,
  RemainingCountBox,
  RemainingCount,
  RemainingUnit,
  ChargeButton,
  ButtonRow,
  CancelButton,
  SendButton,
} from './SmsModalStyled';

// ============================================================
// 상수
// ============================================================

const MAX_PHOTO_COUNT = 3;
// 백엔드가 발송 시 jpg로 자동 변환·압축(≤200KB)하므로, 프론트는 업로드 과대 파일만 차단 (2MB)
const MAX_PHOTO_SIZE_BYTES = 2 * 1024 * 1024;
const SMS_MAX_BYTES = 90; // 90바이트(한글 30자) 초과 시 백엔드가 장문(LMS)으로 자동 전송

// 메시지 타입별 1건당 차감 건수 (백엔드 SmsTokenService와 동일)
const TOKEN_COST = { SMS: 1, LMS: 3, MMS: 6 };

// 백엔드 ImageIO가 디코드 가능한 형식만 허용 (webp 제외 — 변환 단계에서 실패)
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];

// 템플릿에서 사용 가능한 개인화 변수 목록
const TEMPLATE_VARIABLES = ['[회원명]', '[회원권 명]', '[업체명]', '[지점명]', '[결제금액]', '[결제일시]'];

// ============================================================
// 순수 헬퍼 함수 (외부 의존성 없음)
// ============================================================

// '010-1234-5678' → '01012345678'
const normalizePhone = phone => phone?.replace(/-/g, '') || '';

// UTF-8 기준 바이트 길이 (한글은 3바이트)
const getByteLength = text => new TextEncoder().encode(text).length;

// 템플릿에 [회원명] 같은 개인화 변수가 하나라도 포함되어 있는지
const needsPersonalization = template => TEMPLATE_VARIABLES.some(variable => template.includes(variable));

// 템플릿의 변수들을 특정 회원의 데이터로 치환
const replaceTemplateVariables = (template, member, ctx) => {
  if (!template) return '';
  const valueByVariable = {
    '[회원명]': member?.name ?? '',
    '[회원권 명]': member?.ticketName ?? '',
    '[업체명]': ctx.companyName,
    // 발송하는 지점 이름으로 고정한다. 회원 응답의 branchName 은 상품 소유지점이라,
    // 본점이 상품을 갖고 지점이 공유해 파는 구성에서는 지점에서 보낸 문자에 본점 이름이 찍혔다.
    '[지점명]': ctx.branchName,
    '[결제금액]': formatterCurrency(member?.totalPaymentAmount) ?? '',
    '[결제일시]': member?.lastPaymentDate ?? '',
  };
  return TEMPLATE_VARIABLES.reduce(
    (result, variable) => result.split(variable).join(valueByVariable[variable]),
    template,
  );
};

// ============================================================
// 메인 컴포넌트
// ============================================================

const SmsModal = ({ open, onClose, selectedMembers = [], mode = 'MEMBER' }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const fileInputRef = useRef(null);

  // ---- state ----
  const [messageContent, setMessageContent] = useState('');
  const [isAdMessage, setIsAdMessage] = useState(false);
  const [attachedPhotos, setAttachedPhotos] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 실제 발송 대상. 목록에서 넘어온 선택으로 시작해 '수신자 편집'으로 조정한다.
  // 편집 결과는 회원목록의 체크 상태로 되돌리지 않는다 — 목록은 그대로 두고 수신자만 조정한다.
  const [recipients, setRecipients] = useState([]);
  const [recipientEditOpen, setRecipientEditOpen] = useState(false);

  // ---- 파생값 ----
  const branchId = user?.branch?.branchId;
  const branchName = user?.branch?.branchName || '';
  const companyName = user?.headBranch?.headBranchName || '';
  const selectedCount = recipients.length;
  // 전화번호가 빈 회원은 백엔드가 요청 전체를 거절하므로 발송 대상에서 빼고 확인 모달에서 안내한다
  const sendableRecipients = recipients.filter(m => normalizePhone(m.phoneNumber));
  const noPhoneRecipients = recipients.filter(m => !normalizePhone(m.phoneNumber));
  const templateContext = { branchName, companyName };
  const isPhotoLimitReached = attachedPhotos.length >= MAX_PHOTO_COUNT;

  // 광고성 수신동의 판정. 회원목록은 '동의'/'미동의' 문자열을 내려준다.
  // 잠재회원 목록 응답에는 이 필드가 없어 사전 판정이 불가하므로 null(모름)로 둔다.
  const adConsentOf = member => {
    if (member?.isSmsReceive === '동의') return true;
    if (member?.isSmsReceive === '미동의') return false;
    return null;
  };

  const adConsentKnown = recipients.some(m => adConsentOf(m) !== null);

  // ---- 서버 상태: SMS 토큰 잔액 + 지점 SMS 설정(발신/수신거부 번호) ----
  const { data: tokenData } = useQuery({
    queryKey: ['tokenDataKey', branchId],
    queryFn: () => getSmsBalance(branchId, user?.token),
    enabled: !!branchId && !!user?.token,
  });
  const denyPhoneNo = tokenData?.denyPhoneNo || '';

  // ---- 발송 타입/차감 건수 추정 (백엔드 판정과 동일 규칙: 사진→MMS, 90byte 초과→LMS) ----
  // 광고 문자는 머리말·무료거부가 붙은 최종 본문 길이로 판정 (백엔드 formatAdMessage와 동일 구성)
  const sampleMessage = recipients[0]
    ? replaceTemplateVariables(messageContent, recipients[0], templateContext)
    : messageContent;
  const composedMessage = isAdMessage
    ? `(광고) ${branchName}\n${sampleMessage}\n무료거부 ${denyPhoneNo}`
    : sampleMessage;
  const composedByteLength = getByteLength(composedMessage);
  const messageType =
    attachedPhotos.length > 0 ? 'MMS' : composedByteLength > SMS_MAX_BYTES ? 'LMS' : 'SMS';
  const perCost = TOKEN_COST[messageType];
  const totalCost = perCost * sendableRecipients.length;

  // ---- 인증된 발신번호 목록 (발송 시 필수 선택) ----
  const { data: verifiedSenders = [] } = useQuery({
    queryKey: ['verifiedSenders', branchId],
    queryFn: () => getVerifiedSmsSenders(branchId, user?.token),
    enabled: !!branchId && !!user?.token,
  });

  const senderOptions = verifiedSenders.map(sender => ({
    value: sender.id,
    label: `${sender.senderName} (${sender.senderNumber})`,
  }));

  // 인증된 발신번호가 로드되면 첫 번째를 기본 선택
  useEffect(() => {
    if (verifiedSenders.length > 0 && !selectedSenderId) {
      setSelectedSenderId(verifiedSenders[0].id);
    }
  }, [verifiedSenders, selectedSenderId]);

  // ---- 첨부 사진이 바뀔 때마다 미리보기 URL 생성/정리 ----
  useEffect(() => {
    if (attachedPhotos.length === 0) {
      setPhotoPreviewUrls([]);
      return;
    }
    const urls = attachedPhotos.map(file => URL.createObjectURL(file));
    setPhotoPreviewUrls(urls);
    // 언마운트 또는 사진 변경 시 ObjectURL 해제 (메모리 누수 방지)
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [attachedPhotos]);

  // ---- 모달을 열 때 목록의 선택을 수신자로 가져온다 ----
  // open 시점에만 동기화한다. 이후 편집한 수신자를 목록 선택이 덮어쓰면 안 된다.
  useEffect(() => {
    if (open) setRecipients(selectedMembers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ============================================================
  // 사진 첨부 핸들러
  // ============================================================

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFileSelector = () => fileInputRef.current?.click();

  const handleFileChange = e => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // 1. 형식 검증
    const hasInvalidType = files.some(f => !ALLOWED_IMAGE_TYPES.includes(f.type));
    if (hasInvalidType) {
      toast.error(t('JPG, PNG, GIF, BMP 형식만 첨부 가능합니다'));
      resetFileInput();
      return;
    }

    // 2. 크기 검증
    const hasOversized = files.some(f => f.size > MAX_PHOTO_SIZE_BYTES);
    if (hasOversized) {
      toast.error(t('사진은 2MB 이하만 첨부 가능합니다'));
      resetFileInput();
      return;
    }

    // 3. 개수 제한 내에서만 추가
    setAttachedPhotos(prev => {
      const remaining = MAX_PHOTO_COUNT - prev.length;
      if (remaining <= 0) {
        toast.error(t(`사진은 최대 ${MAX_PHOTO_COUNT}장까지 첨부 가능합니다`));
        return prev;
      }
      if (files.length > remaining) {
        toast.error(t(`사진은 최대 ${MAX_PHOTO_COUNT}장까지 첨부 가능합니다`));
      }
      return [...prev, ...files.slice(0, remaining)];
    });

    resetFileInput();
  };

  const handleRemovePhoto = index => {
    setAttachedPhotos(prev => prev.filter((_, i) => i !== index));
    resetFileInput();
  };

  // ============================================================
  // 미리보기
  // ============================================================

  // 첫 번째 수신자 기준으로 변수 치환한 메시지 본문 반환 (실제는 회원별로 다르게 발송)
  // 광고 헤더/푸터는 JSX에서 별도로 렌더링한다
  const getPreviewMessageBody = () => {
    if (!messageContent) return '';
    const sampleMember = recipients[0];
    return sampleMember ? replaceTemplateVariables(messageContent, sampleMember, templateContext) : messageContent;
  };

  // ============================================================
  // 발송 로직
  // ============================================================

  // 첨부된 사진을 S3에 업로드하고 URL 배열 반환
  const uploadAttachedPhotos = async () => {
    if (attachedPhotos.length === 0) return [];
    const res = await uploadImages(attachedPhotos, user?.token);
    return res?.imageUrls || [];
  };

  // 변수가 있는 경우: 회원별 개인화 메시지로 순차 발송
  // subject는 비워 보낸다 — 장문 전환은 백엔드가 본문 길이로 자동 처리(LMS 승격)
  const sendPersonalizedSms = async imageUrls => {
    let succeeded = 0;
    let excluded = 0;
    const failures = [];

    for (const member of sendableRecipients) {
      const personalizedMessage = replaceTemplateVariables(messageContent, member, templateContext);
      try {
        const res = await sendSms(
          branchId,
          {
            senderNumberId: selectedSenderId,
            phoneNo: normalizePhone(member.phoneNumber),
            message: personalizedMessage,
            subject: '',
            imageUrls,
            sendTime: '',
            advertisement: isAdMessage,
          },
          user?.token,
        );
        // 광고 미동의 회원은 200(sendCount=0, excludedCount=1)으로 응답 — 성공으로 세지 않음
        succeeded += res?.sendCount || 0;
        excluded += res?.excludedCount || 0;
      } catch (err) {
        failures.push({ phone: member.phoneNumber, err });
      }
    }

    if (failures.length > 0) console.warn('SMS 일부 실패:', failures);

    return {
      succeeded,
      failed: failures.length,
      excluded,
      total: sendableRecipients.length,
    };
  };

  // 변수가 없는 경우: 1회 bulk 호출로 모두에게 동일 메시지 발송
  const sendBulkSms = async imageUrls => {
    const res = await sendSmsBulk(
      branchId,
      {
        senderNumberId: selectedSenderId,
        receivers: sendableRecipients.map(m => ({ phoneNo: normalizePhone(m.phoneNumber) })),
        message: messageContent,
        subject: '',
        imageUrls,
        sendTime: '',
        advertisement: isAdMessage,
      },
      user?.token,
    );

    const total = sendableRecipients.length;
    const succeeded = res?.sendCount || 0;
    const excluded = res?.excludedCount || 0;
    const failed = Math.max(0, total - succeeded - excluded);

    return { succeeded, failed, excluded, total };
  };

  const sendMutation = useMutation({
    mutationFn: async () => {
      const imageUrls = await uploadAttachedPhotos();
      // 변수 있으면 개인화 발송(N번), 없으면 bulk(1번)
      return needsPersonalization(messageContent) ? sendPersonalizedSms(imageUrls) : sendBulkSms(imageUrls);
    },
    onSuccess: ({ succeeded, failed, excluded, total }) => {
      if (failed === 0 && excluded === 0) {
        toast.success(t(`${total}명에게 전송되었습니다`));
      } else if (failed === 0 && excluded > 0) {
        toast.success(t(`${succeeded}명 전송 완료 (${excluded}명 수신 미동의 제외)`));
      } else if (succeeded === 0) {
        toast.error(t(`전송 실패 (${failed}/${total})`));
      } else {
        toast.warning(t(`${succeeded}/${total}명 전송 완료 (${failed}명 실패)`));
      }
      queryClient.invalidateQueries({ queryKey: ['tokenDataKey'] });
      handleClose();
    },
    onError: () => {
      setConfirmOpen(false);
      toast.error(t('발송에 실패했습니다. 잠시 후 다시 시도해주세요'));
    },
  });

  // ============================================================
  // 발송/닫기/템플릿 핸들러
  // ============================================================

  const handleSend = () => {
    if (!messageContent.trim()) {
      toast.error(t('문자 내용을 입력해주세요'));
      return;
    }
    if (selectedCount === 0) {
      toast.error(t('수신자를 선택해주세요'));
      return;
    }
    // 발신번호는 백엔드에서 필수 + 인증(VERIFIED)된 것만 허용
    if (!selectedSenderId) {
      toast.error(t('발신번호를 선택해주세요. 인증된 발신번호가 없으면 SMS 발신번호 등록에서 등록·승인이 필요합니다'));
      return;
    }
    // 광고 문자는 백엔드에서 무료수신거부 번호가 없으면 발송 거절하므로 사전 차단
    if (isAdMessage && !denyPhoneNo) {
      toast.error(t('지점 SMS 설정에 무료수신거부 번호를 먼저 등록해주세요'));
      return;
    }
    // 검증 통과 → 토큰 차감/타입 확인 다이얼로그 노출 (확인 시에만 실제 발송)
    setConfirmOpen(true);
  };

  const handleClose = () => {
    setMessageContent('');
    setIsAdMessage(false);
    setAttachedPhotos([]);
    setConfirmOpen(false);
    setRecipientEditOpen(false);
    onClose();
  };

  const handleSelectTemplate = content => {
    setMessageContent(prev => (prev ? `${prev}\n${content}` : content));
  };

  // ============================================================
  // JSX
  // ============================================================

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          {/* 헤더 */}
          <ModalHeader>
            <ModalHeaderTitle>{t('문자 보내기')}</ModalHeaderTitle>
            <CloseIconButton onClick={handleClose}>
              <CloseIcon style={{ fontSize: '24px' }} />
            </CloseIconButton>
          </ModalHeader>

          <ModalContentArea>
            <ContentRow>
              {/* 왼쪽: 문자 설정 */}
              <LeftSection>
                {/* 수신번호 */}
                <FormRow>
                  <FormLabel>{t('수신번호')}</FormLabel>
                  <SelectBox>
                    {selectedCount > 0 ? (
                      <SelectText>
                        {selectedCount}
                        {t('명 선택됨')}
                      </SelectText>
                    ) : (
                      <PlaceholderText>{t('회원을 선택해주세요')}</PlaceholderText>
                    )}
                    <ViewListButton onClick={() => setRecipientEditOpen(true)}>
                      {t('수신자 편집')}
                    </ViewListButton>
                  </SelectBox>
                  {isAdMessage && !adConsentKnown && selectedCount > 0 && (
                    <AdExcludeNotice>
                      <AdExcludeHint>
                        {t('수신동의 정보가 목록에 없어 미리 계산할 수 없습니다. 미동의 대상은 발송 시 자동 제외됩니다.')}
                      </AdExcludeHint>
                    </AdExcludeNotice>
                  )}
                </FormRow>

                {/* 발신자 정보 */}
                <FormRow>
                  <FormLabel>{t('발신자 정보')}</FormLabel>
                  <SelectBox>
                    <SelectText>{branchName || t('지점 정보 없음')}</SelectText>
                    <KeyboardArrowDownIcon style={{ color: '#a6a6a6' }} />
                  </SelectBox>
                </FormRow>

                {/* 발신 번호 (인증된 발신번호 중 선택, 필수) */}
                <FormRow>
                  <FormLabel>{t('발신 번호')}</FormLabel>
                  {senderOptions.length > 0 ? (
                    <CommonSelect
                      value={selectedSenderId}
                      onChange={e => setSelectedSenderId(e.target.value)}
                      options={senderOptions}
                      width="100%"
                    />
                  ) : (
                    <SelectBox>
                      <PlaceholderText>{t('발신 번호를 등록해 주세요')}</PlaceholderText>
                    </SelectBox>
                  )}
                </FormRow>

                {/* 내용 */}
                <ContentSection>
                  <ContentTitleRow>
                    <FormLabel>{t('내용')}</FormLabel>
                    <TemplateButton onClick={() => setTemplateModalOpen(true)}>
                      <TemplateButtonText>{t('템플릿 불러오기')}</TemplateButtonText>
                    </TemplateButton>
                  </ContentTitleRow>
                  <MessageTextArea
                    multiline
                    rows={7}
                    placeholder={t('문자 내용을 여기에 입력해주세요.')}
                    value={messageContent}
                    onChange={e => setMessageContent(e.target.value)}
                  />
                  {/* 실시간 바이트/타입 안내 — 90byte 초과 시 장문(LMS) 전환 표시 */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '6px',
                      marginTop: '4px',
                      fontSize: '12px',
                      color: messageType === 'SMS' ? '#999' : '#e57373',
                    }}>
                    <span>{composedByteLength} byte</span>
                    <span>·</span>
                    <span>
                      {messageType === 'SMS'
                        ? t('단문(SMS) · 1건 차감')
                        : messageType === 'LMS'
                        ? t('장문(LMS) 전환 · 3건 차감')
                        : t('사진문자(MMS) · 6건 차감')}
                    </span>
                  </div>
                  <AdCheckboxRow>
                    <AdCheckbox checked={isAdMessage} onChange={e => setIsAdMessage(e.target.checked)} />
                    <AdCheckboxLabel>{t('광고성 문자')}</AdCheckboxLabel>
                  </AdCheckboxRow>
                </ContentSection>

                {/* 사진 첨부 (MMS) */}
                <PhotoRow>
                  <PhotoLabelRow>
                    <FormLabel style={{ width: 'auto' }}>{t('사진 첨부')}</FormLabel>
                  </PhotoLabelRow>
                  <PhotoFieldColumn>
                    <PhotoAttachArea>
                      <PhotoDataBox>
                        {attachedPhotos.length === 0 ? (
                          <PhotoDataText>{t('첨부된 사진이 없습니다.')}</PhotoDataText>
                        ) : (
                          <>
                            <PhotoCountBadge>
                              {attachedPhotos.length}/{MAX_PHOTO_COUNT}
                            </PhotoCountBadge>
                            <PhotoChipList>
                              {attachedPhotos.map((photo, index) => (
                                <PhotoChip key={`${photo.name}-${index}`}>
                                  <PhotoChipText title={photo.name}>{photo.name}</PhotoChipText>
                                  <PhotoRemoveButton onClick={() => handleRemovePhoto(index)}>
                                    <CloseIcon style={{ fontSize: '14px' }} />
                                  </PhotoRemoveButton>
                                </PhotoChip>
                              ))}
                            </PhotoChipList>
                          </>
                        )}
                      </PhotoDataBox>
                      <PhotoAttachButton variant="outlined" onClick={openFileSelector} disabled={isPhotoLimitReached}>
                        {t('사진첨부')}
                      </PhotoAttachButton>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                      />
                    </PhotoAttachArea>
                    <PhotoSpecHint>{t('※ JPG·PNG·GIF·BMP · 2MB 이하 · 최대 3장 (자동 변환)')}</PhotoSpecHint>
                  </PhotoFieldColumn>
                </PhotoRow>
              </LeftSection>

              {/* 오른쪽: 미리보기 + 잔여건수 */}
              <RightSection>
                <PreviewContainer>
                  <PreviewTitle>{t('미리보기')}</PreviewTitle>
                  <PreviewContent>
                    {photoPreviewUrls.length > 0 && (
                      <PreviewImageList>
                        {photoPreviewUrls.map((url, index) => (
                          <PreviewImage key={url} src={url} alt={`preview-${index}`} />
                        ))}
                      </PreviewImageList>
                    )}
                    {isAdMessage && (
                      <PreviewAdMark>
                        {t('(광고)')}
                        {branchName}
                      </PreviewAdMark>
                    )}
                    <PreviewText>{getPreviewMessageBody()}</PreviewText>
                    {isAdMessage && (
                      <PreviewAdFooter>
                        {t('무료수신거부')} {denyPhoneNo || t('(미등록)')}
                      </PreviewAdFooter>
                    )}
                  </PreviewContent>
                </PreviewContainer>

                <RemainingRow>
                  <RemainingInfo>
                    <RemainingLabel>{t('잔여건수')}</RemainingLabel>
                    <RemainingCountBox>
                      <RemainingCount>{tokenData?.balance}</RemainingCount>
                      <RemainingUnit>{t('건')}</RemainingUnit>
                    </RemainingCountBox>
                  </RemainingInfo>
                  <ChargeButton variant="outlined" onClick={() => setChargeModalOpen(true)}>
                    {t('충전하기')}
                  </ChargeButton>
                </RemainingRow>
              </RightSection>
            </ContentRow>

            {/* 하단 버튼 */}
            <ButtonRow>
              <CancelButton variant="contained" onClick={handleClose}>
                {t('취소')}
              </CancelButton>
              <SendButton variant="contained" onClick={handleSend} disabled={sendMutation.isPending}>
                {sendMutation.isPending ? t('발송중...') : t('보내기')}
              </SendButton>
            </ButtonRow>
          </ModalContentArea>
        </ModalContainer>
      </Modal>

      <SmsSendConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => sendMutation.mutate()}
        onCharge={() => {
          setConfirmOpen(false);
          setChargeModalOpen(true);
        }}
        messageType={messageType}
        recipientCount={sendableRecipients.length}
        noPhoneMembers={noPhoneRecipients}
        perCost={perCost}
        totalCost={totalCost}
        balance={tokenData?.balance || 0}
        isAd={isAdMessage}
        loading={sendMutation.isPending}
      />

      <SmsChargeModal open={chargeModalOpen} onClose={() => setChargeModalOpen(false)} balance={tokenData?.balance} />

      <SmsTemplateSelectModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSelect={handleSelectTemplate}
      />

      <MemberRecipientPickerModal
        open={recipientEditOpen}
        onClose={() => setRecipientEditOpen(false)}
        onApply={setRecipients}
        mode={mode}
        initialRecipients={recipients}
        title="문자 수신자 편집"
        perCost={perCost}
        balance={tokenData?.balance ?? null}
      />
    </>
  );
};

export default SmsModal;
